import { Controller } from '@hotwired/stimulus';

// One flash item (fire/shared/_flash_item, docs/DESIGN.md § The flash).
//
//   data-controller="flash" data-flash-auto-value="5000"
//
// auto > 0: it leaves by itself after that many ms, and the clock waits while
// the pointer or focus is on it. 0 stays until closed or the next page. Esc on
// it and `data-action="flash#close"` (a host's X) close it now. Leaving is
// `data-state="out"` for a host's CSS to animate, then the element is removed:
// at its transitionend / animationend, or after 400ms if nothing moves, or at
// once under prefers-reduced-motion.
export default class extends Controller {
  static values = { auto: Number };

  connect() {
    this.remaining = this.autoValue;
    this.held = new Set();
    this.listeners = {
      mouseenter: () => this.hold('pointer'),
      mouseleave: () => this.release('pointer'),
      focusin: () => this.hold('focus'),
      focusout: (event) => {
        if (!this.element.contains(event.relatedTarget)) this.release('focus');
      },
      keydown: (event) => {
        if (event.key === 'Escape') this.close();
      },
    };
    Object.entries(this.listeners).forEach(([type, fn]) => this.element.addEventListener(type, fn));
    this.dock = this.dock.bind(this);
    this.dock();
    document.fonts.ready.then(this.dock); // the title moves when its font lands
    window.addEventListener('resize', this.dock); // a phone turned, a window narrowed
    this.run();
  }

  disconnect() {
    Object.entries(this.listeners).forEach(([type, fn]) => this.element.removeEventListener(type, fn));
    window.removeEventListener('resize', this.dock);
    clearTimeout(this.timer);
    clearTimeout(this.gone);
  }

  close() {
    if (this.closing) return;
    this.closing = true;
    clearTimeout(this.timer);
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return this.element.remove();

    this.element.dataset.state = 'out';
    const done = (event) => {
      if (!event || event.target === this.element) this.element.remove();
    };
    this.element.addEventListener('transitionend', done);
    this.element.addEventListener('animationend', done);
    this.gone = setTimeout(done, 400);
  }

  // Under lg a stack docked in the top half sits just under the page's first
  // h1, then lower in half-rem steps while it would cover a button, link or
  // input, never past the viewport (docs/DESIGN.md § The flash). No room: it
  // stays under the title and covers body text. A stack the host docks at the
  // foot is the mirror, at every width: where its CSS rests it, or on the
  // phone's dock (a visible nav.dock) when that stands higher, then higher in
  // the same steps while it would cover a control. No room: where it rested.
  dock() {
    const stack = this.element.closest('#toasts');
    if (!stack) return;

    stack.style.top = '';
    stack.style.bottom = '';
    const box = stack.getBoundingClientRect();
    const foot = box.top > window.innerHeight / 2;
    if (!foot && !window.matchMedia('(width < 64rem)').matches) return;

    // Laid-out sizes (offset*), not rects: a pill arriving is mid-animation, scaled.
    const wide = Math.max(...[...stack.children].map((el) => el.offsetWidth));
    const left = box.left + (box.width - wide) / 2;
    const right = left + wide;
    // The pills' own span: a host's margins around them are not covered ground.
    const first = stack.firstElementChild;
    const last = stack.lastElementChild;
    const inset = first.offsetTop;
    const height = last.offsetTop + last.offsetHeight - inset;
    const controls = [...document.querySelectorAll('a[href], button, input, select, textarea, summary')]
      .filter((el) => !stack.contains(el) && el.checkVisibility({ visibilityProperty: true }))
      .map((el) => el.getBoundingClientRect());
    const covers = (top) =>
      controls.some((r) => r.left < right && r.right > left && r.top < top + height && r.bottom > top);

    if (foot) {
      const bar = document.querySelector('nav.dock');
      const rest = Math.min(box.bottom, bar && bar.checkVisibility() ? bar.getBoundingClientRect().top : Infinity);
      const tail = box.height - last.offsetTop - last.offsetHeight; // under the last pill
      let bottom = rest;
      while (covers(bottom - tail - height) && bottom - tail - height - 8 > 0) bottom -= 8;
      stack.style.bottom = `${window.innerHeight - (covers(bottom - tail - height) ? rest : bottom)}px`;
      return;
    }
    const title = [...document.querySelectorAll('h1')].find((el) => !stack.contains(el) && el.checkVisibility());
    if (!title) return;

    const under = Math.max(box.top + inset, title.getBoundingClientRect().bottom);
    let top = under;
    while (covers(top) && top + height + 8 < window.innerHeight) top += 8;
    stack.style.top = `${(covers(top) ? under : top) - inset}px`;
  }

  // The clock: runs while nothing holds the item, with what is left of it.
  run() {
    if (this.autoValue <= 0 || this.timer || this.held.size > 0 || this.closing) return;
    this.startedAt = Date.now();
    this.timer = setTimeout(() => this.close(), Math.max(this.remaining, 0));
  }

  hold(why) {
    if (this.held.size === 0 && this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
      this.remaining -= Date.now() - this.startedAt;
    }
    this.held.add(why);
  }

  release(why) {
    this.held.delete(why);
    this.run();
  }
}
